import { FC, lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { SpinnerOverlay } from './components/OverlaySpinner'

// import { MainScreen } from './sections/MainScreen'
import { Menu } from './sections/Menu' // Menu is the most used section. Delaying it affects ux
// import { Order } from './sections/Order'
import { Wishlist } from './featureSliced/padges/Wishlist'
import { Restaurants } from './sections/Restaurants'
import { NetworkError } from 'featureSliced/padges/NetworkError' // NetworkError must be loaded in case there is not connection
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { handleLocalStorage } from './featureSliced/shared/utils/handleLocalStorage'

import { Bucket } from 'featureSliced/padges/Bucket'
import { MainScreen } from 'featureSliced/padges/MainScreen'
import { Payment } from 'featureSliced/padges/Payment'
import { CheckoutQRM } from 'featureSliced/padges/CheckoutQRM'

const TableReservation = lazy(
  (): any => import('featureSliced/padges/TableReservation')
)

const Wallet = lazy((): any => import('./featureSliced/padges/Wallet'))
const Welcome = lazy((): any => import('./featureSliced/padges/Welcome'))

type AppRouterProps = {
  initialLoading?: () => void
  initLoadMenuData?: () => Promise<void>
}

export const AppRouter: FC<AppRouterProps> = ({
  initialLoading,
  initLoadMenuData,
}) => {
  const paymentVariant = useMySelector((state) => state.app.menu.paymentVariant)
  const { bookingVariant } = useMySelector((state) => state.app.menu)
  const { registerId, dataEnc } = handleLocalStorage(['registerId', 'dataEnc'])

  const handleInitialLoading = async () => {
    initialLoading && (await initialLoading())
  }

  const handleInitLoadMenuData = async () => {
    initLoadMenuData && (await initLoadMenuData())
  }

  return (
    <Suspense fallback={<SpinnerOverlay isLoading />}>
      <Routes>
        <Route path="/" element={<MainScreen />} />
        <Route path="/restaurants" element={<Restaurants />} />
        <Route path="/network-error" element={<NetworkError />} />

        <Route
          path="/wishlist"
          element={
            <PrivateRoute isAllowed={registerId || dataEnc}>
              <Wishlist />
            </PrivateRoute>
          }
        />
        <Route
          path="/welcome"
          element={<Welcome loadApp={handleInitialLoading} />}
        />
        <Route
          path="/reservation"
          element={
            <PrivateRoute isAllowed={process.env.REACT_APP_IS_CLIC === '0'}>
              <TableReservation loadApp={handleInitialLoading} />
            </PrivateRoute>
          }
        />
        <Route
          path="/booking"
          element={
            <PrivateRoute isAllowed={process.env.REACT_APP_IS_CLIC === '0'}>
              <TableReservation loadApp={handleInitialLoading} />
            </PrivateRoute>
          }
        />
        <Route
          path="/order"
          // create QR-Menu order. no payment
          element={
            <PrivateRoute isAllowed={process.env.REACT_APP_IS_CLIC === '0'}>
              <Bucket />
            </PrivateRoute>
          }
        />

        <Route
          path="/checkout"
          // pay for QR-Menu order if it is not yet created
          // or regular CC checkout
          element={
            <PrivateRoute
              isAllowed={
                // unavailable for pay AFTER meal
                paymentVariant !== 2 && process.env.REACT_APP_IS_CLIC === '0'
              }
            >
              <CheckoutQRM />
            </PrivateRoute>
          }
        />

        <Route
          path="/payment"
          // pay for QR-Menu order if it was already createed
          element={
            <PrivateRoute
              isAllowed={
                // unavailable for pay BEFORE meal
                paymentVariant !== 1 || process.env.REACT_APP_IS_CLIC === '1'
              }
            >
              <Payment />
            </PrivateRoute>
          }
        />

        <Route
          path="/menu"
          element={
            <PrivateRoute
              isAllowed={
                bookingVariant !== 3 || process.env.REACT_APP_IS_CLIC === '1'
              }
            >
              <Menu loadMenuData={handleInitLoadMenuData} />
            </PrivateRoute>
          }
        />

        <Route
          path="/wallet"
          element={
            <PrivateRoute isAllowed={process.env.REACT_APP_IS_CLIC === '1'}>
              <Wallet />
            </PrivateRoute>
          }
        >
          <Route path="" />
          <Route path="card" />
          <Route path="orders" />
          {/* <Route path="refill" /> */}
          <Route path="scanner" />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

const PrivateRoute: FC<any> = ({ isAllowed = true, children }) => {
  if (isAllowed) {
    return children
  }

  return <Navigate to="/" />
}
